import { InterventionTypeResponse } from '@fullyops/legacy/data/api/types/Intervention';
import {
  Component,
  Input,
  EventEmitter,
  Output,
  OnInit,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { PartUncataloguedControllerV2 } from '../../ui-part-uncatalogued-controller-v2.service';
import { ApiTenantConfigurationService } from '@fullyops/data/tenant-configuration/api-adapter';
import { ApiTypedTenantConfigurationService } from '@fullyops/data/tenant-configuration/typed-api-adapter';
import { PartControllerV2 } from '../../ui-part-controller-v2.service';
import { UiAuthService } from '../../ui-auth.service';
import { PermissionsType } from '@fullyops/legacy/ui/ui-shared/utils/crm-types';
import { InterventionTypeController } from '../../ui-intervention-type-controller-v2.service';
import { SupportTicketResponse } from '@fullyops/legacy/data/api/types/SupportTicket';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import { UncataloguedPartPostRequest } from '@fullyops/legacy/data/api/types/UncataloguedPart';
import { SidenavPortalBridgeService } from '@fullyops/core/sidenav-form-portal-bridge.service';
import { MiniAsset } from '@fullyops/data/assets/asset';
import { ActionTypeLabel } from '@fullyops/data/action-types/entities/action-type-entity';
import {
  ACTION_FORM_TOKEN,
  ActionFormComponent,
} from '@fullyops/features/work-orders/containers/action-form/action-form.component';
import { Part, UncataloguedPart } from '@fullyops/data/parts/parts';
import { WorkOrderActionsService } from '@fullyops/domain/work-order-actions/work-order-actions.service';
import { WorkOrderAction } from '@fullyops/data/work-order-actions/work-order-action';
import { AnomaliesService } from '@fullyops/domain/anomalies/anomalies.service';
import { MatDialog } from '@angular/material/dialog';
import { createInlineAsset } from '@fullyops/shared/create-inline-asset';
import {
  InstallationDeleteDialogComponent,
  InstallationDeleteDialogData,
} from '@fullyops/features/work-orders/containers/installation-delete-dialog/installation-delete-dialog.component';
import { TenantResolverService } from '@fullyops/shared/services/tenant/tenant-resolver.service';
import { assetsTooltipKey } from '@fullyops/shared/assets-tooltip';

@Component({
  selector: 'crm-work-order-actions',
  templateUrl: './work-order-actions.component.html',
  styleUrls: ['./work-order-actions.component.scss'],
  standalone: false,
})
export class WorkOrderActionsComponent implements OnInit, OnDestroy {
  actionTypes$ = new BehaviorSubject<Array<InterventionTypeResponse>>([]);
  partsOptions$ = new BehaviorSubject<Part[]>([]);
  uncataloguedPartsOptions$ = new BehaviorSubject<UncataloguedPart[]>([]);
  permissionToCreateAction: PermissionsType[] = ['CAN_CREATE_INTERVENTIONS'];

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private actionService: WorkOrderActionsService,
    protected partUncataloguedControllerV2: PartUncataloguedControllerV2,
    private tenantConfigurationController: ApiTenantConfigurationService,
    protected partControllerV2: PartControllerV2,
    public auth: UiAuthService,
    public interventionTypeController: InterventionTypeController,
    private anomalyService: AnomaliesService,
    private sidenavPortalBridgeService: SidenavPortalBridgeService,
    private dialog: MatDialog,
    private tenantResolver: TenantResolverService,
    private typedTenantConfigurationApi: ApiTypedTenantConfigurationService,
  ) {}
  @Output() openRequestTab = new EventEmitter<any>();
  @Input() ticketId: string;
  @Input() ticket$: BehaviorSubject<SupportTicketResponse>;
  @Input() equipments: Observable<MiniAsset[]>;
  @Input() isDetailPage: boolean;

  @Input() openedIndex: number;

  protected anomaliesOptions$ = this.anomalyService.anomalies$.pipe(
    map((as) => as.map((a) => ({ id: a.id, name: a.description }))),
  );

  showUncataloguedPart$ = new BehaviorSubject<boolean>(null);

  // Defaults to the back end's own default, so the form behaves correctly for
  // the moment before the configuration arrives.
  private installationOwnershipAutomation = true;

  // Also defaults to the tenant default, which is what applies in the moment
  // before the configuration arrives.
  private installationRequiresUnassignedAsset = true;

  ngOnInit(): void {
    this.ticket$.subscribe((wo) =>
      this.actionService.preload(wo.interventions),
    );
    this.anomalyService.refresh();
    this.typedTenantConfigurationApi.getAssetConfig().subscribe((cfg) => {
      this.installationOwnershipAutomation =
        cfg.installationOwnershipAutomation;
      this.installationRequiresUnassignedAsset =
        cfg.installationRequiresUnassignedAsset;
    });
    this.initPartsList();
    this.initActionTypes();
    this.setUncataloguedPartConfiguration();
    this.ticket$.subscribe((wo) => {
      if (wo.equipments.length > 0) {
        this.addEquipmentChoices(...wo.equipments);
      } else {
        this.equipments.subscribe((eqs) => this.addEquipmentChoices(...eqs));
      }
    });
  }

  ngOnDestroy() {
    this.actionService.clear();
    this.anomalyService.clear();
  }

  protected actions$ = this.actionService.actions$;

  setUncataloguedPartConfiguration() {
    this.tenantConfigurationController
      .getConfigurations({ groupLabel: 'SUPPORT_TICKET_PIPELINE' })
      .subscribe((res) => {
        const filteredConfigurations = res.results.filter(({ deprecated }) =>
          deprecated ? false : true,
        );

        const ENABLE_UNCATALOGUED_PARTS = filteredConfigurations.find(
          ({ label }) => label == 'ENABLE_UNCATALOGUED_PARTS',
        );

        if (ENABLE_UNCATALOGUED_PARTS.value == 'true') {
          this.showUncataloguedPart$.next(true);
          this.initUncataloguedPartOptions();
        } else {
          this.showUncataloguedPart$.next(false);
        }
      });
  }

  gotoTop() {
    document
      .querySelector('.mat-sidenav-content')
      .scrollTo({ behavior: 'smooth', left: 0, top: 0 });
  }

  initPartsList() {
    this.partControllerV2
      .getPartsOptionsList$(partQueryParams)
      .subscribe((res) => {
        this.partsOptions$.next(res);
      });
  }

  initUncataloguedPartOptions() {
    this.partUncataloguedControllerV2
      .getUncataloguedPartsOptionsList$(partQueryParams)
      .subscribe((res) => {
        this.uncataloguedPartsOptions$.next(res);
      });
  }

  initActionTypes() {
    this.interventionTypeController
      .getInterventionType$({})
      .subscribe((res) => {
        this.actionTypes$.next(res);
      });
  }

  createUncataloguedPart(name: string): Observable<string> {
    const newFreePart: UncataloguedPartPostRequest = { name };

    return this.partUncataloguedControllerV2
      .create({ newFreePart })
      .pipe(map((resp) => resp.id));
  }

  private confirmInstallationDelete(
    dialogData: InstallationDeleteDialogData,
  ): Promise<boolean> {
    return new Promise((resolve) => {
      this.dialog
        .open<
          InstallationDeleteDialogComponent,
          InstallationDeleteDialogData,
          boolean
        >(InstallationDeleteDialogComponent, { data: dialogData })
        .afterClosed()
        .subscribe(resolve);
    });
  }

  private actionSaveObserver() {
    return {
      next: () => this.resetView(),
      // A rejected action is reported by the error interceptor, and the form is
      // left open with the user's input so that it can be corrected.
      error: () => {},
    };
  }

  private equipmentsChoices$ = new BehaviorSubject<MiniAsset[]>([]);

  private addEquipmentChoices(...eqs: MiniAsset[]): void {
    this.equipmentsChoices$.next([...this.equipmentsChoices$.value, ...eqs]);
  }

  /**
   * An equipment created here has to stay unassigned where the picker is
   * restricted to unassigned ones, because one created already assigned would not
   * be listed here and the server would refuse the action. In that configuration
   * the installation automation assigns it when the action is saved.
   */
  private createEquipment(
    interventionType: ActionTypeLabel,
  ): Observable<MiniAsset> {
    return createInlineAsset(
      // The action form is itself a sidebar panel.
      { sidebar: this.sidenavPortalBridgeService, stacked: true },
      {
        interventionType,
        requiresUnassignedAsset: this.installationRequiresUnassignedAsset,
        companyId: this.ticket$.value.company?.id ?? null,
      },
    ).pipe(tap((eq) => this.addEquipmentChoices(eq)));
  }

  private isEquipmentRequiredForTenant(): boolean {
    // TODO: implement a proper front-end per-tenant customization system
    return this.tenantResolver.getActiveTenant() !== 'pap2025';
  }

  openActionForm(
    workOrder: SupportTicketResponse,
    intervention: WorkOrderAction,
  ) {
    this.sidenavPortalBridgeService.openPortal(
      ActionFormComponent,
      ACTION_FORM_TOKEN,
      {
        initialValue: intervention,
        equipmentRequired:
          workOrder.interventionType !== 'GENERAL' &&
          this.isEquipmentRequiredForTenant(),
        // An installation action freezes its equipment once it has one, so that
        // the claim it made cannot be silently moved to another machine. An
        // action created without an equipment can still be given its first one,
        // and nothing is frozen at all when the automation is turned off.
        canChangeEquipment:
          workOrder.interventionType !== 'INSTALLATION' ||
          intervention == null ||
          intervention.equipment == null ||
          !this.installationOwnershipAutomation,
        equipmentChoices$: this.equipmentsChoices$,
        equipmentsTooltipKey: assetsTooltipKey(
          workOrder.interventionType,
          this.installationRequiresUnassignedAsset,
        ),
        anomalyChoices$: this.anomaliesOptions$,
        partChoices$: this.partsOptions$,
        uncataloguedPartChoices$: this.uncataloguedPartsOptions$,
        onCancel: () => this.closeForm(),
        onSave:
          intervention == null
            ? (action, attachments) =>
                this.actionService
                  .create(workOrder.id, action, attachments)
                  .subscribe(this.actionSaveObserver())
            : (action, attachments) =>
                this.actionService
                  .update(workOrder.id, intervention.id, action, attachments)
                  .subscribe(this.actionSaveObserver()),
        onDelete: async () => {
          // Whether the equipment is released depends on installation actions in
          // other work orders too, so only the back end can answer it. If it
          // cannot, the delete is abandoned rather than performed unannounced;
          // the failed request is reported by the error interceptor.
          let releasesEquipment: boolean;
          try {
            ({ releasesEquipment } = await this.actionService.getDeletionImpact(
              workOrder.id,
              intervention.id,
            ));
          } catch {
            return;
          }
          if (releasesEquipment) {
            const confirmed = await this.confirmInstallationDelete({
              equipmentName: intervention.equipment?.equipment?.name,
              companyName: workOrder.company.name,
            });
            if (!confirmed) {
              return;
            }
          }
          this.actionService
            .delete(workOrder.id, intervention.id)
            .subscribe(() => this.resetView());
        },

        showAnomalies:
          workOrder.interventionType === 'CORRECTIVE' ||
          workOrder.interventionType === 'GENERAL',
        showUncataloguedParts: this.showUncataloguedPart$.value,
        onCreateEquipment:
          workOrder.equipments.length > 0
            ? null
            : () => this.createEquipment(workOrder.interventionType),
        onCreateAnomaly: (description) =>
          this.anomalyService.create(description),
        onCreateUncataloguedPart: (name) => this.createUncataloguedPart(name),
        onRequestPart: () => {
          this.sidenavPortalBridgeService.closePortal();
          this.changeDetectorRef.checkNoChanges();
          this.openRequestTab.emit();
        },
      },
    );
  }

  closeForm() {
    this.sidenavPortalBridgeService.closePortal();
  }

  private resetView() {
    this.closeForm();
    this.gotoTop();
  }
}

const partQueryParams = { size: 999999, orderBy: 'NAME' } as const;

export const newEquipmentKey = 'NEW';
